import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { Plan } from '@/features/subscription/types/plan';

import {
  cancelPlan,
  clearPlan,
  clearPlanDetails,
  clearPlanMembers,
  fetchPlan,
  fetchPlanDetails,
  fetchPlanMembers,
  setPlan,
  setPlanDetails,
  setPlanLoading,
  undoCancelPlan,
} from '@/store/slices/planSlice';
import { fetchPrices } from '@/store/slices/paymentSlice';
import useAuth from '@/hooks/useAuth';
import { useEffect, useState } from 'react';
import { planService } from '../services/planService';
import { transformSubscriptionToPlanDetails } from '../utils/paddle/PaddleTypeTransformer';
import { User } from '@/types/models/user';
import { COMMON_EVENTS, logHubxEvent } from '@/app/providers/analytics';

export const usePlan = () => {
  const dispatch = useAppDispatch();
  const { user, refreshModelUser } = useAuth();
  const {
    plan,
    planDetails,
    planMembers,
    isActive,
    isLoading,
    error,
    message,
  } = useAppSelector((store) => store.plan);

  const [isOwner, setIsOwner] = useState<boolean>(user?.uid === plan?.ownerId);
  const [remainingSeats, setRemainingSeats] = useState<number>(0);

  useEffect(() => {
    if (plan) {
      setRemainingSeats(plan.seats - Object.keys(plan.members).length);
    }
  }, [plan]);

  useEffect(() => {
    if (user && plan) {
      if (user.uid === plan.ownerId) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
    } else {
      setIsOwner(false);
    }
  }, [user, plan]);

  /**
   * Fetches the user's plan.
   */
  const handleFetchPlan = async (user: User) => {
    if (user && user.planUid) {
      // Clear plan state before fetching new data
      // dispatch(clearPlan());
      await dispatch(fetchPlan(user.planUid)).unwrap();
    }

    dispatch(setPlanLoading(false));
    return null; // Return null if no planUid
  };

  /**
   * Fetches the user's plan details (subscription).
   */
  const handleFetchPlanDetails = async () => {
    if (user && user.planUid && plan) {
      return await dispatch(fetchPlanDetails(plan)).unwrap();
    } else {
      return null;
    }
  };

  /**
   * Cancels the user's plan.
   */
  const handleCancelPlan = async () => {
    if (plan) {
      const cancelEventParams = {
        provider: 'paddle',
        source: 'webapp',
        subscriptionId: plan.subscriptionId,
      };

      logHubxEvent({
        eventName: COMMON_EVENTS.BTN_CANCEL,
        event_params: cancelEventParams,
      });

      try {
        const result = await dispatch(cancelPlan(plan)).unwrap();

        logHubxEvent({
          eventName: COMMON_EVENTS.CANCEL_RESPONDED,
          event_params: { ...cancelEventParams, status: result.status },
        });
      } catch (error: any) {
        logHubxEvent({
          eventName: COMMON_EVENTS.CANCEL_RESPONDED,
          event_params: { ...cancelEventParams, status: error?.status },
        });

        throw error;
      }

      await dispatch(fetchPrices(user?.customerId)).unwrap();
    }
    // void (await handleFetchPlan());
  };

  /**
   * Uncancels the user's plan.
   */
  const handleUndoCancelPlan = async () => {
    if (plan) {
      await dispatch(undoCancelPlan(plan)).unwrap();
    }
    // void (await handleFetchPlan());
  };

  const handleFetchPlanMembers = async () => {
    if (plan) {
      await dispatch(fetchPlanMembers(plan)).unwrap();
    }
  };

  const handleInvitePlanMembers = async (emails: string[]) => {
    if (plan && user) {
      return await planService.inviteMembers(user.email, plan.uid, emails);
    }
  };
  const handleInviteVerification = async (inviteToken: string) => {
    return await planService.verifyInvite(inviteToken);
  };
  const handleInviteAccept = async (inviteToken: string) => {
    return await planService.acceptInvite(inviteToken);
  };

  const handleRemovePlanMembers = async (emails: string[]) => {
    if (plan) {
      return await planService.removeMembers(plan.uid, emails);
    }
  };

  const handleRemoveInvitedMembers = async (emails: string[]) => {
    if (plan) {
      return await planService.removeInvitedEmails(plan.uid, emails);
    }
  };

  const handleManagePlanSeats = async (seats: number) => {
    if (plan) {
      return await planService.manageMembers(plan.uid, seats);
    }
  };

  const handleRefreshData = async (options = { withUserRefresh: true }) => {
    const { withUserRefresh } = options;
    let currentUser = user;

    if (withUserRefresh) {
      // Fetch the latest user data to get potential planUid
      const refreshedUserResponse = await refreshModelUser();
      currentUser = refreshedUserResponse?.user ?? currentUser;
    }

    if (!currentUser || !currentUser.planUid) return;

    // Await the fetchPlan thunk
    const fetchedPlan = await dispatch(fetchPlan(currentUser.planUid)).unwrap();
    // If fetchPlan was successful and returned a plan, then fetch details
    if (fetchedPlan?.plan) {
      await dispatch(fetchPlanDetails(fetchedPlan.plan)).unwrap();
    }
  };

  const handleSetPlan = (planData?: Plan, subscriptionData?: any) => {
    if (planData) {
      dispatch(setPlan(planData));
    }
    if (subscriptionData) {
      const transformedData = transformSubscriptionToPlanDetails(
        subscriptionData,
        planData?.seats || 0
      );
      dispatch(setPlanDetails(transformedData));
    }
  };

  const handleClearPlan = () => {
    dispatch(clearPlan());
    dispatch(clearPlanDetails());
    dispatch(clearPlanMembers());
  };

  return {
    // States
    plan,
    planDetails,
    planIsActive: isActive, // when subscription status is 'active' or 'trialing'
    planIsLoading: isLoading,
    planIsResolved: plan !== null || !isLoading,
    planError: error,
    planMessage: message,
    isPlanOwner: isOwner,
    planMembers: planMembers,
    planRemainingSeats: remainingSeats,

    // Actions
    fetchPlan: handleFetchPlan,
    fetchPlanDetails: handleFetchPlanDetails,
    fetchPlanMembers: handleFetchPlanMembers,
    cancelPlan: handleCancelPlan,
    undoCancelPlan: handleUndoCancelPlan,
    invitePlanMembers: handleInvitePlanMembers,
    inviteVerification: handleInviteVerification,
    acceptInvitation: handleInviteAccept,
    removePlanMembers: handleRemovePlanMembers,
    removeInvitedMembers: handleRemoveInvitedMembers,
    managePlanSeats: handleManagePlanSeats,
    refreshData: handleRefreshData,
    clearPlan: handleClearPlan,
    setPlan: handleSetPlan,
  };
};
